import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import {
  WizardStep,
  StepProps,
} from "@shared/components/application/navigation/Wizard/WizardStep.web";
import { useCallback, useEffect, useRef } from "react";
import { useWizardContext } from "@shared/contexts/WizardContext/WizardContext";
import ProcessingLoaderContainer, {
  ProcessingLoaderContainerProps,
} from "@app-contexts/public/shared/checkout/components/processing/ProcessingLoader/ProcessingLoaderContainer.web";
import { usePaymentManager } from "@shared/modules/payments/contexts/PaymentManagerContext";
import Confirmation from "@app-contexts/public/shared/checkout/components/confirmation/Confirmation/Confirmation.web";
import Payment from "@app-contexts/public/shared/checkout/components/steps/payment/Payment.web";
import { postMessageToReactNativeWebView } from "@shared/utils/webview";
import { useTranslation } from "@shared/utils/internationalization";

export type PaymentStepProps = {
  spinnerProps?: ProcessingLoaderContainerProps;
  allowRedirect?: boolean;
  customData?: StepProps["customData"];
  onProcessingPurchaseStatusChange?: (isProcessing: boolean) => void;
};

export const PAYMENT_STEP_ID = "payment";

export function PaymentStep({
  spinnerProps,
  allowRedirect = true,
  customData,
  onProcessingPurchaseStatusChange,
}: PaymentStepProps) {
  const publicBasket = usePublicBasket();
  const { hasItems, isExpired } = publicBasket;
  const { triggerError, currentStep, finish } = useWizardContext();
  const { t } = useTranslation("checkout");
  const { purchaseData, isProcessingPurchase, isPurchaseComplete } =
    usePaymentManager();

  useEffect(() => {
    const basketError =
      currentStep?.id === "payment" &&
      !isProcessingPurchase &&
      !purchaseData &&
      (isExpired || !hasItems);

    if (basketError) {
      triggerError();
    }
  }, [hasItems, isExpired, purchaseData]);

  // Prevent the user from navigating back in the progress indicator
  useEffect(() => {
    if (isPurchaseComplete) {
      finish();
      // In react native webview, when purchase is complete send PURCHASE_COMPLETE action
      // to handle native purchase complete cleanup
      postMessageToReactNativeWebView({ action: "PURCHASE_COMPLETE" });
    }
  }, [isPurchaseComplete]);

  useEffect(() => {
    onProcessingPurchaseStatusChange?.(isProcessingPurchase);
  }, [isProcessingPurchase]);

  return (
    <WizardStep
      stepId={PAYMENT_STEP_ID}
      stepName={t("Payment", { ns: "common" })}
      defaultIsValid={false}
      keepMounted
      customData={customData}
    >
      {({ onValid, onInvalid }) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const handleValidationChange = useCallback(
          (valid: boolean) => {
            if (valid) onValid?.();
            else onInvalid?.();
          },
          [onValid, onInvalid]
        );

        return (
          <>
            {isPurchaseComplete ? (
              <Confirmation />
            ) : (
              <>
                <Payment
                  publicBasket={publicBasket}
                  onValidationChange={handleValidationChange}
                  allowRedirect={allowRedirect}
                />
                {isProcessingPurchase && (
                  <ProcessingLoaderContainer
                    containerRef={containerRef}
                    {...spinnerProps}
                  />
                )}
              </>
            )}
          </>
        );
      }}
    </WizardStep>
  );
}

PaymentStep.id = PAYMENT_STEP_ID;
